import { Facet } from '../models/Facet';
import { CancelSource } from '../utilities/async/CancelError';
import { ComputedItemFacet, IComputedItemFacetProps } from '../models/itemCache/ComputedItemFacet';
import { IItemCache } from '../models/IItemCacheObserver';
import { childItemsFacet } from './SetFacets';

// General-purpose facets for things that backround operations may do.

/**
 * Facet which provides a reference to an item created during an operation.
 * @public
 */
export const createdItemFacet: Facet<{
    itemKey: string;
}> = new Facet('createdItem');

/**
 * Facet which provides a reference to the source item used during an operation.
 * @public
 */
export const sourceItemFacet: Facet<{
    itemKey: string;
}> = new Facet('sourceItem');

/**
 * @public
 */
export const destinationItemFacet: Facet<{
    itemKey: string;
}> = new Facet('destinationItem');

/**
 * @beta
 */
export type ConflictResolution =
    'fail' |
    'replace' |
    'rename';

/**
 * @beta
 */
export const conflictResolutionFacet = new Facet<ConflictResolution>('conflictResolution');

/**
 * @public
 */
export type ProgressPhase =
    'created' |
    'started' |
    'completed' |
    'failed' |
    'canceled' |
    'expired';

/**
 * @public
 */
export const ProgressPhase: {[K in ProgressPhase]: K; } = {
    created: 'created',
    started: 'started',
    completed: 'completed',
    failed: 'failed',
    canceled: 'canceled',
    expired: 'expired'
};

/**
 * @public
 */
export type Histogram<T extends string> = {[K in T]: number; };

/**
 * Facet which indicates which phase an operation is in.
 * @public
 */
export const progressPhaseFacet: Facet<ProgressPhase> = new Facet('phase');

/**
 * Facet which marks the incremental status toward completion of a progress item.
 * @public
 */
export const progressSizeFacet: Facet<{
    total: number;
    current: number;
}> = new Facet('progressSize');

/**
 * Facet which defines a source to cancel the operation for a progress item.
 * @public
 */
export const cancelSourceFacet: Facet<CancelSource> = new Facet('cancelSource');

/**
 * @public
 */
export const DEFAULT_PROGRESS_COUNTS: Histogram<ProgressPhase> = {
    created: 0,
    started: 0,
    completed: 0,
    failed: 0,
    canceled: 0,
    expired: 0
};

/**
 * @public
 */
export const DEFAULT_PROGRESS_PHASE = ProgressPhase.created;

/**
 * @public
 */
export const overallProgressCountsFacet: Facet<Histogram<ProgressPhase>> = new ComputedItemFacet('overallProgressCounts', (
    itemCache: IItemCache,
    props: IComputedItemFacetProps
): Histogram<ProgressPhase> => {
    const counts: Histogram<ProgressPhase> = {
        ...DEFAULT_PROGRESS_COUNTS
    };

    const progressPhase = itemCache.demandItemFacet(progressPhaseFacet, props.itemKey);

    if (progressPhase) {
        counts[progressPhase]++;
    }

    const childItems = itemCache.demandItemFacet(childItemsFacet, props.itemKey);

    if (childItems) {
        for (const childItemKey of childItems.itemKeys) {
            const childCounts = itemCache.demandItemFacet(overallProgressCountsFacet, childItemKey);

            if (childCounts) {
                counts.created += childCounts.created;
                counts.started += childCounts.started;
                counts.completed += childCounts.completed;
                counts.failed += childCounts.failed;
                counts.canceled += childCounts.canceled;
                counts.expired += childCounts.expired;
            }
        }
    }

    return counts;
});

/**
 * @public
 */
export const overallProgressPhaseFacet: Facet<ProgressPhase> = new ComputedItemFacet('overallProgressPhase', (
    itemCache: IItemCache,
    props: IComputedItemFacetProps
): ProgressPhase | undefined => {
    const progressCounts = itemCache.demandItemFacet(overallProgressCountsFacet, props.itemKey);

    if (!progressCounts) {
        return undefined;
    }

    return getPhaseFromItemCountsByPhase(progressCounts);
});

/**
 * Determines the exact phase of a parent progress item based on the progress phase totals of its children.
 * @public
 */
export function getPhaseFromItemCountsByPhase(itemCountsByPhase: Histogram<ProgressPhase>): ProgressPhase {
    if (itemCountsByPhase.started) {
        return ProgressPhase.started;
    } else if (itemCountsByPhase.created) {
        return ProgressPhase.created;
    } else if (itemCountsByPhase.failed) {
        return ProgressPhase.failed;
    } else if (itemCountsByPhase.completed) {
        return ProgressPhase.completed;
    } else if (itemCountsByPhase.canceled) {
        return ProgressPhase.canceled;
    } else {
        return ProgressPhase.expired;
    }
}

/**
 * @public
 */
export const overallProgressSizeFacet: Facet<typeof progressSizeFacet.type> = new ComputedItemFacet('overallProgressSize', (
    itemCache: IItemCache,
    props: IComputedItemFacetProps
): typeof progressSizeFacet.type | undefined => {
    const progressSize = itemCache.demandItemFacet(progressSizeFacet, props.itemKey);

    if (progressSize) {
        return progressSize;
    }

    const childItems = itemCache.demandItemFacet(childItemsFacet, props.itemKey);

    if (childItems) {
        const totalProgressSize: typeof progressSizeFacet.type = {
            total: 0,
            current: 0
        };

        for (const childItemKey of childItems.itemKeys) {
            const childProgressSize = itemCache.demandItemFacet(overallProgressSizeFacet, childItemKey);

            if (!childProgressSize) {
                return undefined;
            }

            totalProgressSize.current += childProgressSize.current;
            totalProgressSize.total += childProgressSize.total;
        }

        return totalProgressSize;
    }
});

/**
 * @public
 */
export type SimpleProgressPhase =
    typeof ProgressPhase.started | typeof ProgressPhase.completed | typeof ProgressPhase.canceled | typeof ProgressPhase.failed;

/**
 * @public
 */
export const DEFAULT_SIMPLE_PROGRESS_PHASE = ProgressPhase.started;

/**
 * @public
 */
export interface ISimpleProgressBreakdown {
    /**
     * The effective phase of the operation.
     * Treats 'created' and 'started' as 'started'.
     * Treats 'expired' as 'canceled'.
     */
    phase: SimpleProgressPhase;
    /**
     * The number of items to report as within the given phase.
     */
    count: number;
}

/**
 * @public
 */
export const DEFAULT_SIMPLE_PROGRESS_BREAKDOWN: ISimpleProgressBreakdown = {
    phase: DEFAULT_SIMPLE_PROGRESS_PHASE,
    count: 0
};

/**
 * @public
 */
export const overallSimpleProgressBreakdownFacet: Facet<ISimpleProgressBreakdown> =
    new ComputedItemFacet('overallSimpleProgressBreakdown', (
        itemCache: IItemCache,
        props: IComputedItemFacetProps
    ): ISimpleProgressBreakdown | undefined => {
        const progressCounts = itemCache.demandItemFacet(overallProgressCountsFacet, props.itemKey);

        if (!progressCounts) {
            return undefined;
        }

        return getSimpleProgressBreakdown(progressCounts);
    });

/**
 * Function which consumes a progress state histogram and 'simplifies' the numbers
 * to something useful for producing progress reporting strings.
 * @public
 */
export function getSimpleProgressBreakdown(itemCountsByPhase: Histogram<ProgressPhase>): ISimpleProgressBreakdown {
    if (itemCountsByPhase.started || itemCountsByPhase.created) {
        return {
            phase: ProgressPhase.started,
            count: itemCountsByPhase.started + itemCountsByPhase.completed + itemCountsByPhase.created + itemCountsByPhase.failed
        };
    } else if (itemCountsByPhase.failed) {
        return {
            phase: ProgressPhase.failed,
            count: itemCountsByPhase.failed
        };
    } else if (itemCountsByPhase.completed) {
        return {
            phase: ProgressPhase.completed,
            count: itemCountsByPhase.completed
        };
    } else {
        return {
            phase: ProgressPhase.canceled,
            count: itemCountsByPhase.canceled + itemCountsByPhase.expired
        };
    }
}
